import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { articlesAPI, categoriesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Search, Filter, MoreVertical, Pencil, Trash2, Eye, CheckSquare, Square, Archive, Upload } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  review: 'bg-amber-50 text-amber-700',
  in_review: 'bg-amber-50 text-amber-700',
  sub_edited: 'bg-yellow-50 text-yellow-800',
  legal_review: 'bg-rose-50 text-rose-800',
  approved: 'bg-emerald-50 text-emerald-700',
  scheduled: 'bg-slate-100 text-slate-700',
  published: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-gray-100 text-gray-500',
  killed: 'bg-red-50 text-red-700',
};

const FILTER_STATUSES = ['', 'draft', 'in_review', 'sub_edited', 'legal_review', 'approved', 'scheduled', 'published', 'archived'];
const STATUS_LABEL = {
  '': 'All',
  draft: 'Draft',
  in_review: 'In Review',
  sub_edited: 'Sub-Edited',
  legal_review: 'Legal',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
  archived: 'Archived',
  killed: 'Killed',
};

export default function AdminArticles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // Keep state in sync with URL (?status=) for deep-linking from dashboard pipeline widget.
  useEffect(() => {
    const urlStatus = searchParams.get('status') || '';
    if (urlStatus !== statusFilter) setStatusFilter(urlStatus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    loadArticles();
    categoriesAPI.list().then(r => setCategories(r.data.categories || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await articlesAPI.list(params);
      setArticles(res.data.articles || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadArticles();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await articlesAPI.delete(id);
      toast.success('Article deleted');
      loadArticles();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await articlesAPI.update(id, { status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      loadArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) { toast.error('Select articles first'); return; }
    try {
      await articlesAPI.bulk({ article_ids: selectedIds, action });
      toast.success(`Bulk ${action} completed (${selectedIds.length} articles)`);
      setSelectedIds([]);
      loadArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedIds.length === articles.length) setSelectedIds([]);
    else setSelectedIds(articles.map(a => a.id));
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.name || '';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Articles</h1>
          <p className="text-sm text-[#5B616E] mt-1">{total} total articles</p>
        </div>
        <Link to="/admin/articles/new">
          <Button className="bg-[#121316] text-white hover:bg-[#1B1D22]">
            <Plus className="w-4 h-4 mr-2" /> New Article
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search articles..."
            className="w-full pl-9 pr-4 py-2 border border-[#E7E0D6] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#B89B5E] text-sm"
          />
        </form>
        <div className="flex gap-2">
          {FILTER_STATUSES.map(status => (
            <button
              key={status || 'all'}
              onClick={() => {
                setStatusFilter(status);
                setPage(1);
                const next = new URLSearchParams(searchParams);
                if (status) next.set('status', status); else next.delete('status');
                setSearchParams(next, { replace: true });
              }}
              className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                statusFilter === status
                  ? 'bg-[#121316] text-white border-[#121316]'
                  : 'bg-white text-[#5B616E] border-[#E7E0D6] hover:bg-[#E7E0D6]'
              }`}
            >
              {STATUS_LABEL[status] || status}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-[#121316] text-white rounded-xl" data-testid="bulk-action-bar">
          <span className="text-sm">{selectedIds.length} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => handleBulkAction('publish')}><Upload className="w-3 h-3 mr-1" /> Publish</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => handleBulkAction('archive')}><Archive className="w-3 h-3 mr-1" /> Archive</Button>
            <Button size="sm" variant="ghost" className="text-[#B3261E] hover:bg-white/10" onClick={() => handleBulkAction('delete')}><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
            <Button size="sm" variant="ghost" className="text-white/60 hover:text-white" onClick={() => setSelectedIds([])}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="bg-white border-[#E7E0D6] overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">
            {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="admin-articles-table" className="w-full">
              <thead>
                <tr className="border-b border-[#E7E0D6] bg-[#FBF7EF]">
                  <th className="w-10 px-4 py-3"><button onClick={toggleAll}>{selectedIds.length === articles.length && articles.length > 0 ? <CheckSquare className="w-4 h-4 text-[#121316]" /> : <Square className="w-4 h-4 text-[#5B616E]" />}</button></th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3">Title</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden md:table-cell">Category</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden sm:table-cell">Author</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden lg:table-cell">Views</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden lg:table-cell">Date</th>
                  <th className="text-right text-xs font-medium text-[#5B616E] px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map(article => (
                  <tr key={article.id} data-testid="admin-table-row" className="border-b border-[#E7E0D6] hover:bg-[#FBF7EF]/50 transition-colors">
                    <td className="px-4 py-3"><button onClick={() => toggleSelect(article.id)}>{selectedIds.includes(article.id) ? <CheckSquare className="w-4 h-4 text-[#121316]" /> : <Square className="w-4 h-4 text-[#5B616E]" />}</button></td>
                    <td className="px-4 py-3">
                      <div className="max-w-xs">
                        <p className="text-sm font-medium truncate">{article.title}</p>
                        {article.subtitle && <p className="text-xs text-[#5B616E] truncate mt-0.5">{article.subtitle}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-[#5B616E]">{article.category?.name || getCategoryName(article.category_id) || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge data-testid="article-status-badge" className={`text-[10px] ${STATUS_STYLES[article.status] || ''}`}>
                        {article.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-xs text-[#5B616E]">{article.author_name || '—'}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-xs text-[#5B616E]">{(article.views || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-xs text-[#5B616E]">{new Date(article.created_at).toLocaleDateString()}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid="admin-row-actions" className="p-1 hover:bg-[#E7E0D6] rounded-lg">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/articles/${article.id}/edit`}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </Link>
                          </DropdownMenuItem>
                          {article.status === 'published' && (
                            <DropdownMenuItem asChild>
                              <Link to={`/article/${article.id}`} target="_blank">
                                <Eye className="w-4 h-4 mr-2" /> View
                              </Link>
                            </DropdownMenuItem>
                          )}
                          {article.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(article.id, 'review')}>
                              Submit for Review
                            </DropdownMenuItem>
                          )}
                          {article.status === 'review' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(article.id, 'published')}>
                              Publish
                            </DropdownMenuItem>
                          )}
                          {article.status === 'published' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(article.id, 'archived')}>
                              Archive
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(article.id)}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {articles.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-sm text-[#5B616E]">
                      No articles found. <Link to="/admin/articles/new" className="text-[#1E5A8A] underline">Create your first article</Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                page === i + 1 ? 'bg-[#121316] text-white' : 'bg-white border border-[#E7E0D6] hover:bg-[#E7E0D6]'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
